import { useRef, useState } from 'react';
import { Button, TableRowData } from 'tdesign-react';

import { IFormItemProps, ITableColumns } from '@/components';
import { FormTitle } from '@/components/form/materials/Title';
import DialogForm from '@/components/form/wrapper/DialogForm';
import { NovaImage } from '@/components/Image';
import { CoverName } from '@/components/list';
import { CoverVideo } from '@/components/list/coverVideo';
import SimpleSortTable from '@/components/table/simpleSortTable';
import { HomeDataManagerType } from '@/const/promotion';
import { ImageRatio } from '@/const/richMedia';
import { IStory } from '@/interface';
import { OthersData, Story } from '@/interface/promotion';
import { StoryCard } from '@/pages/promotion/cards/storyCard';

import { LIST_FORM_CONFIG } from './form-config';
import styles from './index.module.less';

interface IProps {
  data: Story['story'],
  othersData: Story['others'];
  managerType?: HomeDataManagerType;
  moveStory: (id: string) => void;
  onChange: (data: TableRowData) => void;
  othersChange: (data: OthersData[]) => void
}

const List: React.FC<IProps> = ({
  data = [],
  moveStory,
  onChange,
  othersChange,
  othersData = [],
  managerType = HomeDataManagerType.HOMEDATAMANAGERTYPE_DEFAULT,
}) => {
  const tableRef = useRef(null);
  const MAX_TABLE_WIDTH = 1400;
  const columns = [
    {
      colKey: 'story',
      title: '故事',
      width: MAX_TABLE_WIDTH * 0.2,
      cell: ({ row }: { row: IStory }) => <StoryCard data={row} />,
    },
    {
      colKey: 'bgImage',
      title: '封面图',
      width: MAX_TABLE_WIDTH * 0.15,
      cell: ({ row }: { row: IStory }) => {
        const data = getOtherData(row?.id);
        return (
          <CoverName
            coverProps={{
              src: data?.bgImage,
            }} />
        );
      },
    },
    {
      colKey: 'bgVideo',
      title: '封面视频',
      width: MAX_TABLE_WIDTH * 0.15,
      cell: ({ row }: { row: IStory }) => {
        const data = getOtherData(row?.id);
        return (
          <CoverVideo
            coverProps={{
              src: data?.bgVideo,
            }} />
        );
      },
    },
    {
      colKey: 'leftLabel',
      title: '左上角图标',
      width: MAX_TABLE_WIDTH * 0.1,
      cell: ({ row }: { row: IStory }) => {
        const data = getOtherData(row?.id);
        return (
          <>
            <div className={styles.labelText}>{data?.leftLabel?.text}</div>
            <NovaImage
              style={{ maxWidth: 160, maxHeight: 40, overflow: 'scroll' }}
              src={data?.leftLabel?.icon}
            />
          </>
        );
      },
    },
    {
      colKey: 'op',
      title: '操作',
      width: MAX_TABLE_WIDTH * 0.1,
      align: 'center' as 'center',
      cell: ({ row }: { row: IStory }) => (
        <div className={styles.options}>
          <Button
            onClick={() => handleEdit(row)}
            theme="default"
            variant="text"
          >
            新增配置
          </Button>
          <Button
            onClick={() => moveStory(row.id)}
            theme="default"
            variant="text"
          >
            移除
          </Button>
        </div>
      ),
    },
  ];

  const getOtherData = (id: string) => othersData.find(e => e?.id === id);

  const handleEdit = (row: IStory) => {
    const data = othersData.find(e => e.id === row?.id);
    setFormData({ ...data, id: row?.id });
    setVisible(true);
  };

  const getData = (data: Array<Record<string, any>>) => {
    onChange(data);
  };

  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({ id: '' });

  const onSubmit = async (values: OthersData) => {
    const hasValue = othersData?.some(e => e?.id === values?.id);
    const temp = othersData?.map((e) => {
      if (e.id === values.id) {
        return values;
      }
      return e;
    });
    othersChange(hasValue ? temp : [...temp, values]);
  };

  const handleClose = () => {
    setFormData({ id: '' });
    setVisible(false);
  };

  const onValuesChange = (content: Record<string, unknown>) => {
    setFormData({ ...formData, ...content });
  };

  const refactorFieldsConfig = (configs: IFormItemProps[]) => {
    const isBanner = managerType === HomeDataManagerType.HOMEDATAMANAGERTYPE_BANNER;
    return (
      configs?.map((e) => {
        if (isBanner && e?.props === 'bgImage') {
          return {
            ...e,
            uiOptions: {
              ...e?.uiOptions,
              width: 150,
              height: 267,
              limit: { ratio: ImageRatio['9:16'] },
            },
          };
        }
        return e;
      })
    );
  };

  return (
    <>
      <SimpleSortTable
        cRef={tableRef}
        columnsConfig={columns as unknown as ITableColumns[]}
        dataList={data}
        dragWidth={100}
        getData={getData}
        extendsConfig={{}}
      />
      <DialogForm
        fieldsConfig={LIST_FORM_CONFIG}
        onSubmit={onSubmit}
        onClose={handleClose}
        isVisible={Boolean(visible && formData?.id)}
        formData={{ ...formData }}
        formConfig={{
          layout: 'vertical',
          labelWidth: 120,
          labelAlign: 'top',
          onValuesChange,
        }}
        refactorFieldsConfig={configs => refactorFieldsConfig(configs)}
        layOutConfig={{
          paddingBottom: 30,
          maxHeight: 700,
        }}
        dialogConfig={{
          header: (
            <FormTitle
              title={'新增配置'}
              replaceObject={{}}
            />
          ),
          style: { width: 800 },
          destroyOnClose: true,
        }}
        topRender={
          <div className={styles.tipText14}>
            该配置展示优先级高于故事默认配置，如设置了封面图，则封面图将覆盖故事默认封面图
          </div>
        }
      />
    </>
  );
};

export default List;
